import {
    collection,
    query,
    where,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    getDoc,
    orderBy,
    serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

// Collection names
const COLLECTIONS = {
    ADMINS: 'admins',
    MANAGERS: 'managers',
    SUB_MANAGERS: 'sub_managers'
};

// Role definitions
export const ROLES = {
    SUPER_ADMIN: 'super_admin',
    MANAGER: 'manager',
    SUB_MANAGER: 'sub_manager'
};

// Permission definitions
export const PERMISSIONS = {
    CUSTOMERS: {
        VIEW: 'customers:view',
        DELETE: 'customers:delete'
    },
    SERVICEMEN: {
        VIEW: 'servicemen:view',
        DELETE: 'servicemen:delete',
        REVIEW: 'servicemen:review',
        APPROVE: 'servicemen:approve'
    },
    USERS: {
        CREATE: 'users:create',
        MANAGE: 'users:manage'
    },
    CASHBACKS: {
        VIEW: 'cashbacks:view',
        MANAGE: 'cashbacks:manage'
    },
    REFERRALS: {
        VIEW: 'referrals:view',
        MANAGE: 'referrals:manage'
    }
};

// Role-based permissions
const rolePermissions = {
    [ROLES.SUPER_ADMIN]: [
        PERMISSIONS.CUSTOMERS.VIEW,
        PERMISSIONS.CUSTOMERS.DELETE,
        PERMISSIONS.SERVICEMEN.VIEW,
        PERMISSIONS.SERVICEMEN.DELETE,
        PERMISSIONS.SERVICEMEN.REVIEW,
        PERMISSIONS.SERVICEMEN.APPROVE,
        PERMISSIONS.USERS.CREATE,
        PERMISSIONS.USERS.MANAGE,
        PERMISSIONS.CASHBACKS.VIEW,
        PERMISSIONS.CASHBACKS.MANAGE,
        PERMISSIONS.REFERRALS.VIEW,
        PERMISSIONS.REFERRALS.MANAGE
    ],
    [ROLES.MANAGER]: [
        PERMISSIONS.CUSTOMERS.VIEW,
        PERMISSIONS.CUSTOMERS.DELETE,
        PERMISSIONS.SERVICEMEN.VIEW,
        PERMISSIONS.SERVICEMEN.DELETE,
        PERMISSIONS.SERVICEMEN.REVIEW,
        PERMISSIONS.SERVICEMEN.APPROVE,
        PERMISSIONS.CASHBACKS.VIEW,
        PERMISSIONS.CASHBACKS.MANAGE,
        PERMISSIONS.REFERRALS.VIEW,
        PERMISSIONS.REFERRALS.MANAGE
    ],
    [ROLES.SUB_MANAGER]: [
        PERMISSIONS.CUSTOMERS.VIEW,
        PERMISSIONS.SERVICEMEN.VIEW,
        PERMISSIONS.SERVICEMEN.REVIEW,
        PERMISSIONS.SERVICEMEN.APPROVE
    ]
};

// Authentication functions
export const authenticateUser = async (username, password) => {
    try {
        // Check in admins collection first (your existing admin collection)
        const adminQuery = query(
            collection(db, COLLECTIONS.ADMINS),
            where('username', '==', username),
            where('password', '==', password)
        );
        const adminSnapshot = await getDocs(adminQuery);

        if (!adminSnapshot.empty) {
            const adminDoc = adminSnapshot.docs[0];
            const adminData = adminDoc.data();
            return {
                success: true,
                user: {
                    id: adminDoc.id,
                    username: adminData.username,
                    role: ROLES.SUPER_ADMIN,
                    permissions: rolePermissions[ROLES.SUPER_ADMIN],
                    name: adminData.name || adminData.username,
                    email: adminData.email || '',
                    createdDate: adminData.createdDate?.toDate?.()?.toLocaleDateString() || new Date().toLocaleDateString(),
                    lastLogin: new Date().toLocaleDateString()
                }
            };
        }

        // Check in managers collection
        const managerQuery = query(
            collection(db, COLLECTIONS.MANAGERS),
            where('username', '==', username),
            where('password', '==', password)
        );
        const managerSnapshot = await getDocs(managerQuery);

        if (!managerSnapshot.empty) {
            const managerDoc = managerSnapshot.docs[0];
            const managerData = managerDoc.data();
            if (managerData.locked === true || managerData.locked === 'true') {
                return { success: false, error: 'Account is locked. Contact admin to unlock account.' };
            }
            return {
                success: true,
                user: {
                    id: managerDoc.id,
                    username: managerData.username,
                    role: ROLES.MANAGER,
                    permissions: rolePermissions[ROLES.MANAGER],
                    name: managerData.name || managerData.username,
                    email: managerData.email || '',
                    createdDate: managerData.createdDate?.toDate?.()?.toLocaleDateString() || new Date().toLocaleDateString(),
                    lastLogin: new Date().toLocaleDateString()
                }
            };
        }

        // Check in sub_managers collection
        const subManagerQuery = query(
            collection(db, COLLECTIONS.SUB_MANAGERS),
            where('username', '==', username),
            where('password', '==', password)
        );
        const subManagerSnapshot = await getDocs(subManagerQuery);

        if (!subManagerSnapshot.empty) {
            const subManagerDoc = subManagerSnapshot.docs[0];
            const subManagerData = subManagerDoc.data();
            if (subManagerData.locked === true || subManagerData.locked === 'true') {
                return { success: false, error: 'Account is locked. Contact admin to unlock account.' };
            }
            return {
                success: true,
                user: {
                    id: subManagerDoc.id,
                    username: subManagerData.username,
                    role: ROLES.SUB_MANAGER,
                    permissions: rolePermissions[ROLES.SUB_MANAGER],
                    name: subManagerData.name || subManagerData.username,
                    email: subManagerData.email || '',
                    createdDate: subManagerData.createdDate?.toDate?.()?.toLocaleDateString() || new Date().toLocaleDateString(),
                    lastLogin: new Date().toLocaleDateString()
                }
            };
        }

        return {
            success: false,
            error: 'Invalid credentials'
        };
    } catch (error) {
        console.error('Authentication error:', error);
        return {
            success: false,
            error: 'Authentication failed'
        };
    }
};

// User management functions
export const createUser = async (userData, role) => {
    try {
        const collectionName = role === ROLES.MANAGER ? COLLECTIONS.MANAGERS : COLLECTIONS.SUB_MANAGERS;

        const newUser = {
            username: userData.username,
            password: userData.password,
            name: userData.name,
            email: userData.email,
            role: role,
            createdDate: serverTimestamp(),
            lastLogin: null,
            isActive: true
        };

        const docRef = await addDoc(collection(db, collectionName), newUser);

        return {
            success: true,
            id: docRef.id,
            user: { ...newUser, id: docRef.id }
        };
    } catch (error) {
        console.error('Error creating user:', error);
        return {
            success: false,
            error: 'Failed to create user'
        };
    }
};

export const updateUser = async (userId, userData, newRole, oldRole = null) => {
    try {
        // Determine new collection
        const newCollectionName = newRole === ROLES.MANAGER ? COLLECTIONS.MANAGERS : COLLECTIONS.SUB_MANAGERS;

        const updateData = {
            username: userData.username,
            name: userData.name,
            email: userData.email,
            role: newRole,
            updatedDate: serverTimestamp()
        };

        // Only update password if provided
        if (userData.password) {
            updateData.password = userData.password;
        }

        // If oldRole is not provided, try to find the user in both collections
        let oldCollectionName = null;
        if (!oldRole) {
            // Try to find user in managers collection
            const managerDocRef = doc(db, COLLECTIONS.MANAGERS, userId);
            const managerDocSnap = await getDoc(managerDocRef);
            if (managerDocSnap.exists()) {
                oldCollectionName = COLLECTIONS.MANAGERS;
                oldRole = ROLES.MANAGER;
            } else {
                // Try sub_managers collection
                const subManagerDocRef = doc(db, COLLECTIONS.SUB_MANAGERS, userId);
                const subManagerDocSnap = await getDoc(subManagerDocRef);
                if (subManagerDocSnap.exists()) {
                    oldCollectionName = COLLECTIONS.SUB_MANAGERS;
                    oldRole = ROLES.SUB_MANAGER;
                } else {
                    return {
                        success: false,
                        error: 'User not found in any collection'
                    };
                }
            }
        } else {
            // Determine old collection from oldRole
            oldCollectionName = oldRole === ROLES.MANAGER ? COLLECTIONS.MANAGERS : COLLECTIONS.SUB_MANAGERS;
        }

        // If role is changing, we need to move the document to the new collection
        if (oldRole !== newRole) {
            // Get the current document data
            const oldDocRef = doc(db, oldCollectionName, userId);
            const oldDocSnap = await getDoc(oldDocRef);
            
            if (!oldDocSnap.exists()) {
                return {
                    success: false,
                    error: 'User not found in old collection'
                };
            }

            const oldData = oldDocSnap.data();
            
            // Create new document in the new collection with updated data
            const newUserData = {
                ...oldData,
                ...updateData,
                createdDate: oldData.createdDate || serverTimestamp(), // Preserve original created date
                lastLogin: oldData.lastLogin || null
            };
            
            // Remove serverTimestamp() from createdDate if it's already a timestamp
            if (newUserData.createdDate === serverTimestamp()) {
                newUserData.createdDate = oldData.createdDate;
            }

            const newCollectionRef = collection(db, newCollectionName);
            const newDocRef = await addDoc(newCollectionRef, newUserData);

            // Delete the old document
            await deleteDoc(oldDocRef);

            return {
                success: true,
                newId: newDocRef.id
            };
        } else {
            // Role is not changing, just update in the same collection
            await updateDoc(doc(db, oldCollectionName, userId), updateData);

            return {
                success: true
            };
        }
    } catch (error) {
        console.error('Error updating user:', error);
        return {
            success: false,
            error: 'Failed to update user: ' + error.message
        };
    }
};

export const deleteUser = async (userId, role) => {
    try {
        const collectionName = role === ROLES.MANAGER ? COLLECTIONS.MANAGERS : COLLECTIONS.SUB_MANAGERS;

        await deleteDoc(doc(db, collectionName, userId));

        return {
            success: true
        };
    } catch (error) {
        console.error('Error deleting user:', error);
        return {
            success: false,
            error: 'Failed to delete user'
        };
    }
};

export const updateUserLock = async (userId, role, locked) => {
    try {
        const collectionName = role === ROLES.MANAGER ? COLLECTIONS.MANAGERS : COLLECTIONS.SUB_MANAGERS;
        const docRef = doc(db, collectionName, userId);
        await updateDoc(docRef, { locked: !!locked });
        return { success: true };
    } catch (error) {
        console.error('Error updating lock status:', error);
        return { success: false, error: error.message };
    }
};

export const getAllUsers = async () => {
    try {
        const allUsers = [];

        // Get admins
        const adminSnapshot = await getDocs(collection(db, COLLECTIONS.ADMINS));
        adminSnapshot.forEach(doc => {
            const data = doc.data();
            allUsers.push({
                id: doc.id,
                ...data,
                role: ROLES.SUPER_ADMIN,
                createdDate: data.createdDate?.toDate?.()?.toLocaleDateString() || new Date().toLocaleDateString(),
                lastLogin: data.lastLogin?.toDate?.()?.toLocaleDateString() || null
            });
        });

        // Get managers
        const managerSnapshot = await getDocs(collection(db, COLLECTIONS.MANAGERS));
        managerSnapshot.forEach(doc => {
            const data = doc.data();
            allUsers.push({
                id: doc.id,
                ...data,
                role: ROLES.MANAGER,
                createdDate: data.createdDate?.toDate?.()?.toLocaleDateString() || new Date().toLocaleDateString(),
                lastLogin: data.lastLogin?.toDate?.()?.toLocaleDateString() || null
            });
        });

        // Get sub managers
        const subManagerSnapshot = await getDocs(collection(db, COLLECTIONS.SUB_MANAGERS));
        subManagerSnapshot.forEach(doc => {
            const data = doc.data();
            allUsers.push({
                id: doc.id,
                ...data,
                role: ROLES.SUB_MANAGER,
                createdDate: data.createdDate?.toDate?.()?.toLocaleDateString() || new Date().toLocaleDateString(),
                lastLogin: data.lastLogin?.toDate?.()?.toLocaleDateString() || null
            });
        });

        return {
            success: true,
            users: allUsers
        };
    } catch (error) {
        console.error('Error fetching users:', error);
        return {
            success: false,
            error: 'Failed to fetch users'
        };
    }
};

export const updateLastLogin = async (userId, role) => {
    try {
        const collectionName = role === ROLES.SUPER_ADMIN ? COLLECTIONS.ADMINS :
            role === ROLES.MANAGER ? COLLECTIONS.MANAGERS : COLLECTIONS.SUB_MANAGERS;

        await updateDoc(doc(db, collectionName, userId), {
            lastLogin: serverTimestamp()
        });
    } catch (error) {
        console.error('Error updating last login:', error);
    }
};
